<template>
  <div>
    <div v-if="showListComponent">
      <page-header class="mt-6 lg:mt-0" :full-width="true">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full lg:w-1/2 px-2">
            <h1 slot="header" class="text-grey-darker text-xl font-bold leading-normal py-4">
              Property Assets</h1>
          </div>
          <div class="flex w-1/2 pr-4 justify-end items-end">
            <button type="button"
                    class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                    @click="changeView(true)"
            ><i class="fas fa-list"></i></button>
            <button type="button"
                    class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                    @click="changeView(false)"
            >
              <i class="fas fa-image"></i>
            </button>
          </div>
        </div>
      </page-header>

      <div class="w-full flex flex-wrap">
        <div class="flex flex-col w-1/4 pr-2 mb-4 justify-end">
          <label for="address_country" class="text-sm flex flex-row mb-2">Country *</label>
          <select id="address_country" v-model="searchParams.address_country" class="bg-white rounded h-10 py-0 px-2 shadow text-sm border text-grey-darkest" @change="countryChange()">
            <option value="">All Countries</option>
            <option v-for="(option, i) in propertyEnums.fc_country" :key="i" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="flex flex-col w-1/4 pr-2 mb-4 justify-end">
          <label for="office" class="text-sm flex flex-row mb-2">Office</label>
          <v-select
              multiple
              v-model="searchParams.office"
              placeholder="[All Offices]"
              :options="offices"
              :reduce="office => office.id"
              label="name"
              class="bg-white text-sm shadow text-grey-darkest"
              :class="searchParams.office ? (searchParams.office.length === 0  ? 'h-10' : 'h-auto') : 'h-10'"
          >
          </v-select>
        </div>
        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="location" class="text-sm flex flex-row mb-2">Postcode / Location</label>
          <locality-suggestion-box v-model="searchParams.location" :country="searchParams.address_country" ref="location_autocomplete" />
        </div>
        <div class="flex flex-col w-1/4 pr-6 mb-4 justify-end">
          <label for="location" class="text-sm flex flex-row mb-2">Postcode / Location Radius</label>
          <pl-select
            v-model="searchParams.radius_miles"
            name="radius_miles_menu"
            id="radius_miles_menu"
            select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
            class="w-full"
          >
            <option value="">No Postcode / Location Radius</option>
            <option :key="option.value" v-for="option of distanceOptions" :value="option.value"> {{ option.name }}</option>
          </pl-select>
        </div>

        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="department" class="text-sm flex flex-row mb-2">Sale/Let</label>
          <pl-select
            v-model="searchParams.department"
            name="department"
            id="department"
            select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
            class="w-full"
          >
            <option value="">All</option>
            <option value="RS">For Sale</option>
            <option value="RL">To Let</option>
          </pl-select>
        </div>
        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="min_price" class="text-sm flex flex-row mb-2">Min Price</label>
          <pl-select v-model="searchParams.min_price" id="min_price" name="min_price" select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest number-field">
            <option :value="0">No Minimum</option>
            <option v-for="n in priceRange" :key="n" :value="n" v-if="searchParams.max_price === 0 || searchParams.max_price > n">
              {{ n | number }}
            </option>
          </pl-select>
        </div>
        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="max_price" class="text-sm flex flex-row mb-2">Max Price</label>
          <pl-select v-model="searchParams.max_price" id="max_price" name="max_price" select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest number-field">
            <option :value="0">No Maximum</option>
            <option v-for="n in priceRange" :key="n" :value="n" v-if="searchParams.min_price === 0 || searchParams.min_price < n">
              {{ n | number }}
            </option>
          </pl-select>
        </div>
        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="min_bedrooms" class="text-sm flex flex-row mb-2">Bedrooms</label>
          <pl-select
            v-model="searchParams.min_bedrooms"
            name="min_bedrooms"
            id="min_bedrooms"
            select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
            class="w-full"
          >
            <option :key="option.value" v-for="option of bedroomOptions" :value="option.value"> {{ option.name }}</option>
          </pl-select>
        </div>
        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="type_menu" class="text-sm flex flex-row mb-2">Property Type</label>
          <pl-select
            v-model="searchParams.type"
            name="type_menu"
            id="type_menu"
            select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
            class="w-full"
          >
            <option value="">All Types</option>
            <option :key="option.value" v-for="option of propertyGroupOptions" :value="option.value"> {{ option.name }}</option>
          </pl-select>
        </div>
        <div class="flex flex-col w-1/4 pr-4 mb-4 justify-end">
          <label for="status" class="text-sm flex flex-row mb-2">Status</label>
          <pl-select
            v-model="searchParams.status"
            name="status"
            id="status"
            select-class="bg-white rounded h-10 py-0 shadow text-sm text-grey-darkest"
            class="w-full"
          >
            <option value="">All</option>
            <option value="1" v-if="searchParams.department === 'RS'">Available</option>
            <option value="2" v-if="searchParams.department === 'RS'">SSTC</option>
            <option value="4" v-if="searchParams.department === 'RS'">Under Offer</option>
            <option value="6" v-if="searchParams.department === 'RL'">Let Agreed</option>
          </pl-select>
        </div>
        <div class="flex flex-col pr-4 mb-4 justify-end">
          <div>
            <button type="button"
                    class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                    @click="search">
              <i class="fas fa-search"></i> {{this.$route.params.reportId ? 'Get more properties': 'Search'}}
            </button>
            <button type="button"
                    class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                    @click="resetSearch">
              <i class="fas fa-sync"></i> Reset
            </button>
          </div>
        </div>
      </div>
      <panel v-if="!loading && properties && properties.length > 0" class="mt-5 pt-6">
        <strong class="mb-2">{{totalCount}} properties found from the above search criteria.</strong>
      </panel>
      <panel class="mt-5 pt-6" v-if="!loading && properties && properties.length > 0">
        <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
          <table class="table-auto text-center w-full" v-if="briefView">
            <thead class="uppercase text-xs">
            <tr>
              <th class="text-left p-2 whitespace-no-wrap" colspan="2">
                Property
              </th>
              <th class="p-2 whitespace-no-wrap">
                Price
              </th>
              <th class="p-2 whitespace-no-wrap">
                Type
              </th>
              <th class="p-2 whitespace-no-wrap">
                Status
              </th>
              <th class="p-2 whitespace-no-wrap">
                Office
              </th>
              <th class="p-2 whitespace-no-wrap">
                View on Website
              </th>
              <th class="p-2 whitespace-no-wrap">
                Download
              </th>
              <th class="p-2 whitespace-no-wrap">
                Copy Description
              </th>
            </tr>
            </thead>
            <tbody class="text-sm">
            <tr v-for="(property, i) in properties" :key="i"
                class="border-t border-grey-lighter hover:bg-grey-lightest cursor-pointer">
              <td>
                <div class="icon-img w-24 h-16 my-2 rounded"
                     :style="{'background-image': `url('${property.main_photo}')`}" v-if="property.main_photo"></div>
                <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                  <icon name="property_cms" class="text-white"/>
                </div>
              </td>
              <td class="text-left p-2 font-bold" @click="showAssetDetails(property.id)">{{ property.address }}
              </td>
              <td class="p-2">{{ property.listed_price }}</td>
              <td class="p-2">{{ property.type }}</td>
              <td class="p-2">{{ property.status }}</td>
              <td class="p-2">{{ property.office.name }}</td>
              <td class="p-2">
                <a :href="property.link" target="_blank" class="flex-1 text-center py-2 px-6 mb-1 text-sm hover:border-transparent rounded no-underline py-4 mb-2 mx-2" v-if="property.link">
                  <i class="fas fa-eye" />
                </a>
              </td>
              <td class="p-2">
                <loader v-if="downloadingProperties.includes(property.id)" :fillColor="'grey'"/>
                <span v-else>
                  <i class="fas fa-download cursor-pointer" @click="downloadMediasForProperty(property)" title="Download all images and documents" />
                </span>
              </td>
              <td @click="copyDescription(property.description)">
                <i class="fas fa-link mr-1"></i>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="flex flex-wrap" v-else>
            <div v-for="(property, i) in properties" :key="i"
                 class="flex w-full border-t border-grey-lighter p-4">
              <div class="lg:w-1/3 px-8">
                <div v-if="property.media.images">
                  <div class="slider-item" v-for="(image, j) in property.media.images" :key="j" v-if="j < 3" v-show="activeIndex == j">
                    <div class="flex items-center w-full h-64 overflow-hidden">
                      <img class="border" :src="property.media.images[j]['full_url']">
                    </div>
                  </div>
                  <div class="flex items-center text-center justify-center">
                    <span class="p-2" v-for="(image, k) in property.media.images" :key="k" v-if="k < 3" :class="[ k == activeIndex ? 'text-secondary' : 'text-grey-light' ]" @click="activeIndex = k">
                      <i class="fas fa-circle"></i>
                    </span>
                  </div>
                </div>
                <div v-else-if="property.main_photo">
                  <img class="icon-img w-24 h-16 my-2 rounded"
                       :src="`url('${property.main_photo}'`" >
                </div>
                <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                  <icon name="property_cms" class="text-white"/>
                </div>
              </div>
              <div class="flex flex-wrap lg:w-2/3">
                <div class="w-full text-lg py-2"> <a :href="'/property-assets-management/'+property.id" class="text-black no-underline">{{ property.address }}</a></div>
                <div class="flex w-full py-2 border-bottom">
                  <span class="pr-8"><i class="fas fa-bed pr-2"></i> {{ property.bedrooms }}</span>
                  <span class="pr-8"><i class="fas fa-bath pr-2"></i> {{ property.bathrooms }}</span>
                  <span class="pr-8"><i class="fas fa-couch pr-2"></i> {{ property.reception_rooms }}</span>
                </div>
                <div class="flex w-full py-2">
                  <div class="flex flex-wrap w-full">
                    <div class="my-2 w-full">Listed Price: {{ property.listed_price }}</div>
                    <div class="my-2 w-full">Office: {{property.office.name}}</div>
                    <div class="my-2 w-full">Status: {{property.status}}</div>
                    <div class="my-2 w-full">Type: {{ property.type }}</div>
                    <div class="my-2 w-full">
                      <a :href="property.link" target="_blank" class="flex-1 text-center text-grey-dark pr-6 mb-1 text-sm hover:border-transparent rounded no-underline py-4 mb-2 mr-2" v-if="property.link">
                        <i class="fas fa-link" />
                      </a>
                      <i class="fas fa-download cursor-pointer" @click="downloadMediasForProperty(property)" title="Download all images and documents" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <paginator :data="results.meta" @change="pageChanged" />
        </div>
      </panel>

      <panel v-if="!loading && properties && properties.length === 0" class="mt-5 pt-6">
        <strong class="mb-2">No Properties</strong>
        <p>Your search returned no results</p>
      </panel>
      <panel v-else-if="!loading && properties && properties.length === 0" class="mt-5 pt-6">
        <strong class="mb-2">Press Search button to search properties</strong>
      </panel>
      <div v-if="loading" class="w-full h-screen text-center">
        <loader></loader>
      </div>
    </div>
    <div v-else-if="showDetailsComponent" class="mt-8">
      <property-asset-management :selectedPropertyId="selectedPropertyId" @updateAssetComponentVisibility="updateAssetComponentVisibility"/>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api, { v2Api } from '@/http'
import debounce from 'lodash/debounce'
import { bugalowTypeOptions, characterPropertyTypeOptions, commercialPropertyTypeOptions, distanceOptions, flatTypeOptions, houseTypeOptions, landTypeOptions, propertyStatusOptions, bedroomOptions, propertyGroupOptions } from '@/selectOptions'
import JSZip from 'jszip'
import SaveAs from 'file-saver'
import LocalitySuggestionBox from '@/components/LocalitySuggestionBox'
import vSelect from 'vue-select'
import PropertyAssetManagement from '@/views/PropertyAssetManagement.vue'
export default {
  name: 'PropertyAssetsPage',
  components: {
    'v-select': vSelect,
    LocalitySuggestionBox,
    PropertyAssetManagement
  },
  data () {
    let params = {
      location: null,
      radius_miles: '',
      address_country: 'GB',
      department: '',
      min_price: 0,
      max_price: 0,
      status: '',
      min_bedrooms: null,
      type: '',
      listed_in: null,
      office: null,
      page: 1
    }
    const savedParams = localStorage.getItem('search.assetPage') ? JSON.parse(localStorage.getItem('search.assetPage')) : {}
    Object.assign(params, savedParams)
    return {
      searchParams: params,
      results: {},
      loading: false,
      saving: false,
      briefView: true,
      activeIndex: 0,
      pageHasChanged: false,
      loadingStats: false,
      selectedProperties: [],
      properties: [],
      newReport: {
        id: '',
        title: '',
        error: ''
      },
      bugalowTypeOptions: bugalowTypeOptions,
      characterPropertyTypeOptions: characterPropertyTypeOptions,
      commercialPropertyTypeOptions: commercialPropertyTypeOptions,
      distanceOptions: distanceOptions,
      flatTypeOptions: flatTypeOptions,
      houseTypeOptions: houseTypeOptions,
      landTypeOptions: landTypeOptions,
      propertyStatusOptions: propertyStatusOptions,
      bedroomOptions: bedroomOptions,
      propertyGroupOptions: propertyGroupOptions,
      loadingAllDownload: false,
      loadingSelectedDownload: false,
      downloadingProperties: [],
      offices: [],
      selectedPropertyId: null,
      showListComponent: true,
      showDetailsComponent: false
    }
  },
  computed: {
    ...mapState(['system', 'selectedOffice', 'propertyEnums']),
    priceRange: function () {
      let output = []
      let i = 0
      switch (this.searchParams.department) {
        case 'RL':
          for (i = 1000; i < 1500; i += 100) {
            output.push(i)
          }
          for (i = 1750; i <= 3000; i += 250) {
            output.push(i)
          }
          for (i = 3500; i <= 7000; i += 500) {
            output.push(i)
          }
          for (i = 8000; i <= 10000; i += 1000) {
            output.push(i)
          }
          for (i = 12500; i <= 20000; i += 2500) {
            output.push(i)
          }
          for (i = 25000; i < 40000; i += 5000) {
            output.push(i)
          }
          return output
        default:
          for (i = 250000; i < 500000; i += 10000) {
            output.push(i)
          }
          for (i = 500000; i < 1000000; i += 25000) {
            output.push(i)
          }
          for (i = 1000000; i <= 10000000; i += 50000) {
            output.push(i)
          }
          return output
      }
    },
    authHeaders () {
      return {
        'Authorization': 'Bearer ' + (localStorage.getItem('access') || localStorage.getItem('accessToken'))
      }
    }
  },
  watch: {
    system () {
      this.searchParams.system_id = this.system.id

      if (this.selectedOffice) {
        this.search()
      }
    },
    selectedOffice: {
      handler: function () {
        this.searchParams.office_id = this.selectedOffice.id

        if (this.system) {
          this.search()
        }
      },
      deep: true
    },
    'searchParams.page': function () {
      this.pageHasChanged = true
    },
    searchParams: {
      deep: true,
      handler: function (newVal) {
        this.search()
      }
    }
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading', 'fetchPropertyEnums']),
    search: debounce(function () {
      localStorage.setItem('search.assetPage', JSON.stringify(this.searchParams))
      this.$router.replace({ query: this.searchParams })
      this.loading = true
      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      api.get('/properties-assets-list', {
        params: this.searchParams
      }).then((response) => {
        this.results = response.data
        this.properties = response.data.data
        for (const property of this.properties) {
          property.checked = this.selectedProperties.some(selectedProp => selectedProp.id === property.id)
        }
        this.setPageLoading(false)
        this.loading = false
        this.totalCount = response.data.meta.total
      }, (error) => {
        // Error
        this.loading = false
        this.setPageLoading(false)
        let errors = error.response.data.errors
        let errorMessage = ''
        if (Object.keys(errors)[0] && errors[Object.keys(errors)[0]]) {
          errorMessage += ' ' + errors[Object.keys(errors)[0]]
        }
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: errorMessage
        })
      })
    }, 500),
    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
      this.search()
    },
    selectProperty (property) {
      if (property.checked) {
        // Add property to selectedProperties if it is checked
        this.selectedProperties.push(property)
      } else {
        // Remove property from selectedProperties if it is unchecked
        const index = this.selectedProperties.findIndex(p => p.id === property.id)
        if (index !== -1) {
          this.selectedProperties.splice(index, 1)
        }
      }
    },
    checkAll () {
      if (this.selectedProperties.length === this.properties) {
        this.selectedProperties = []
        this.properties.map((property, index) => {
          property.checked = false
          return property
        })
      } else {
        this.selectedProperties = this.properties
        this.properties.map((property, index) => {
          property.checked = true
          return property
        })
      }
    },
    changeView (flag) {
      this.briefView = flag
    },
    async downloadMediasForProperty (property) {
      let newZip = new JSZip()
      this.downloadingProperties.push(property.id)
      for (let i = 0; i < property.media.images.length; i++) {
        if (property.media.images[i]['full_url']) {
          const response = await fetch(property.media.images[i]['full_url'])
          const blob = await response.blob()
          const extention = property.media.images[i]['full_url'].split('.').pop().toLowerCase()
          let fileName = property.media.images[i].caption ? property.media.images[i].caption + '.' + extention : 'image_' + i + '.' + extention
          newZip.file(fileName, blob)
        }
      }

      for (let i = 0; i < property.media.floorplans.length; i++) {
        if (property.media.floorplans[i]['full_url']) {
          const response = await fetch(property.media.floorplans[i]['full_url'])
          const blob = await response.blob()
          const extention = property.media.floorplans[i]['full_url'].split('.').pop().toLowerCase()
          let fileName = property.media.floorplans[i].caption ? property.media.floorplans[i].caption + '.' + extention : 'floorplan_' + i + '.' + extention
          newZip.file(fileName, blob)
        }
      }

      for (let i = 0; i < property.media.documents.length; i++) {
        if (property.media.documents[i]['full_url']) {
          const response = await fetch(property.media.documents[i]['full_url'])
          const blob = await response.blob()
          let fileName = 'brochure_' + i + '.pdf'
          newZip.file(fileName, blob)
        }
      }

      newZip.generateAsync({ type: 'blob' }).then(content => {
        SaveAs(content, 'property-assets.zip')
      })
      // Remove property from downloadingProperties if it is unchecked
      let index = this.downloadingProperties.indexOf(property.id)
      if (index !== -1) {
        this.downloadingProperties.splice(index, 1)
      }
    },
    async downloadAllAssetsForProperty (property) {
      api.post(`/properties-assets-list/download/${property.id}`, {}, { responseType: 'blob' }).then((response) => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(response.data)
        link.style.display = 'none'
        link.setAttribute('download', 'assets.zip')
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
      }, () => {
        // Error
        this.loading = false
        this.newReport.error = ''
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Unable to download the medias.'
        })
      })
    },
    addressEndpoint (input) {
      return process.env.VUE_APP_ROOT_API + '/v2/localities/suggest?address_country=GB&q=' + input
    },
    async copyDescription (description) {
      try {
        await navigator.clipboard.writeText(description)
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Success',
          info: 'Description copied to clipboard.'
        })
      } catch (e) {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Failed to copy description.'
        })
      }
    },
    resetSearch () {
      this.searchParams = {
        system_id: this.system.id,
        office_id: this.selectedOffice.id,
        address_country: 'GB',
        radius_miles: '',
        department: 'RS',
        min_price: 0,
        max_price: 0,
        min_bedrooms: null,
        type: '',
        status: '',
        location: '',
        office: null,
        page: 1
      }
      this.$refs.location_autocomplete.reset()
      localStorage.removeItem('assetPage')
      this.getOfficeList()
    },
    getOfficeList () {
      v2Api.get('office', {
        params: {
          referral_network_id: this.selectedOffice.network_id,
          limit_by_country: this.searchParams.address_country ? (this.searchParams.address_country.length > 1 ? 1 : 0) : 0,
          country: this.searchParams.address_country ? this.searchParams.address_country : 'GB',
          all: 1
        }
      }).then((response) => {
        this.offices = response.data
      })
    },
    showAssetDetails (propertyId) {
      this.selectedPropertyId = propertyId
      this.showListComponent = false
      this.showDetailsComponent = true
    },
    updateAssetComponentVisibility () {
      this.showListComponent = true
      this.showDetailsComponent = false
    },
    countryChange () {
      this.searchParams.office = null
      this.getOfficeList()
    }
  },
  created () {
    this.$route.query.system_id = this.system.id
    this.$route.query.office_id = this.selectedOffice.id
    Object.assign(this.searchParams, this.$route.query)
    if (localStorage.getItem('search.assetPage')) {
      this.search()
    }
    this.fetchPropertyEnums()
    this.getOfficeList()
  }
}
</script>
<style>
  .icon-img {
    background-size: cover;
    background-position: center;
  }

  .icon {
    width: 80px;
    height: 60px;
  }
  .vs__dropdown-toggle {
    border: none !important;
  }
  .number-field::placeholder {
    color: #3d4852;
    opacity: 1;
  }
  .vs__search {
    color: #000000 !important;
  }
</style>
